import type { PostHog } from 'posthog-js'

import { POSTHOG_API_KEY, POSTHOG_HOST } from '@/libs/config/client'

// posthog-js is ~204KB parsed; a static import put it in the landing page's
// first-load bundle. It is loaded on demand here instead — the type-only
// import above costs nothing at runtime.
let phInstance: PostHog | null = null
let initStarted = false

// Tracker calls that arrive while the SDK is still downloading are queued
// and flushed on init, so lazy-loading cannot drop early events. (The old
// synchronous version simply dropped anything fired before init.)
const pendingCallbacks: Array<(ph: PostHog) => void> = []
const MAX_PENDING = 50

export const initPosthog = (): void => {
  if (typeof window === 'undefined') return
  if (!POSTHOG_API_KEY) return
  if (initStarted) return
  initStarted = true

  // Captured after the guard so the narrowed string type survives into the
  // async closure below.
  const apiKey = POSTHOG_API_KEY

  import('posthog-js')
    .then(({ default: posthog }) => {
      // Wrap in try/catch so an init failure doesn't escape into whatever
      // flow lazy-initialized us. initStarted resets on failure so a later
      // tracker call can retry instead of using a half-initialised SDK.
      try {
        // ui_host pins toolbar / in-app links to PostHog's real UI domain
        // even when api_host points at the Oddle reverse proxy (Sonic).
        // Vercel env per environment:
        //   prod  → NEXT_PUBLIC_POSTHOG_HOST = https://sonic.oddle.me
        //   beta  → NEXT_PUBLIC_POSTHOG_HOST = https://sonic.beta.oddle.me
        //   local → falls back to https://us.i.posthog.com (direct)
        posthog.init(apiKey, {
          api_host: POSTHOG_HOST,
          ui_host: 'https://us.posthog.com',
          person_profiles: 'identified_only',
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: false,
        })
        phInstance = posthog
        // Expose the singleton on window so debugging tools (and the
        // `posthog` global in DevTools console) can reach it.
        ;(window as unknown as { posthog: PostHog }).posthog = posthog
        pendingCallbacks.splice(0).forEach((cb) => {
          try {
            cb(posthog)
          } catch (e) {
            console.error('[posthog]', e)
          }
        })
      } catch (err) {
        console.warn('[posthog] init failed', err)
        initStarted = false
      }
    })
    .catch((err) => {
      console.warn('[posthog] failed to load', err)
      initStarted = false
    })
}

export const safePosthog = (callback: (ph: PostHog) => void): void => {
  if (typeof window === 'undefined') return
  if (!POSTHOG_API_KEY) return
  // Lazy-init: React commit order means child effects (tracker calls)
  // can fire before parent effects (PosthogInit's useEffect). Calling
  // initPosthog() here ensures events fired in that window aren't
  // dropped. initPosthog is idempotent via the local flag.
  if (!initStarted) initPosthog()
  if (!phInstance) {
    // SDK still downloading — queue and flush on init.
    if (pendingCallbacks.length < MAX_PENDING) pendingCallbacks.push(callback)
    return
  }
  try {
    callback(phInstance)
  } catch (e) {
    console.error('[posthog]', e)
  }
}

// In the Oddle Shop OMS data model the account + brand identifiers live
// nested under `meta.menu`. The brand identifier is `entityId` (numeric;
// PostHog group keys must be strings so we coerce). The account is
// `account.id` (uuid). No top-level `meta.account` / `meta.brand` is
// populated at runtime despite what the TS type definition claims.
type ShopMeta = {
  menu?: {
    name?: string | null
    entityId?: number | string | null
    account?: { id?: string | null; name?: string | null } | null
  } | null
} | null

export const accountIdOf = (meta?: ShopMeta) => meta?.menu?.account?.id || null
export const brandIdOf = (meta?: ShopMeta) =>
  meta?.menu?.entityId != null && meta.menu.entityId !== ''
    ? String(meta.menu.entityId)
    : null

export const buildPosthogGroups = (
  meta?: ShopMeta,
): Record<string, string> | undefined => {
  const groups: Record<string, string> = {}
  const aId = accountIdOf(meta)
  const bId = brandIdOf(meta)
  if (aId) groups.account = aId
  if (bId) groups.brand = bId
  return Object.keys(groups).length ? groups : undefined
}

// Intentionally no `setPosthogGroups` / `posthog.group()` helper.
// Shop runs in a consumer browser session, and `posthog.group()`
// permanently associates the consumer's person profile with the
// brand/account for the rest of that session. We only want per-event
// attribution via `$groups` (see `buildPosthogGroups` above). Group
// property names (account name, brand name) are set from the backend
// via groupIdentify, not from a consumer session.
